<!--
  Filter group editor for PaginationFilter.
  Prop-driven: the parent owns the draft and applies the payload. No API calls.
-->
<template>
  <div class="pagination-filter__builder" :data-testid="`${testIdPrefix}__filter-builder`">
    <div class="pagination-filter__top-bar">
      <div class="pagination-filter__logic">
        <span class="pagination-filter__logic-label">
          Grouping logic: match <strong>{{ groupingConnector === connectorAnd ? 'ALL' : 'ANY' }}</strong> of the filter groups
        </span>
        <div class="pagination-filter__logic-toggle">
          <button
            type="button"
            :data-testid="`${testIdPrefix}__grouping-logic-and`"
            :class="['pagination-filter__logic-btn', { 'pagination-filter__logic-btn--active': groupingConnector === connectorAnd }]"
            :disabled="disabled"
            @click="$emit('set-grouping-connector', connectorAnd)"
          >
            AND
          </button>
          <button
            type="button"
            :data-testid="`${testIdPrefix}__grouping-logic-or`"
            :class="['pagination-filter__logic-btn', { 'pagination-filter__logic-btn--active': groupingConnector === connectorOr }]"
            :disabled="disabled"
            @click="$emit('set-grouping-connector', connectorOr)"
          >
            OR
          </button>
        </div>
      </div>
      <button type="button" class="pagination-filter__add-group-btn" :data-testid="`${testIdPrefix}__add-group-btn`" :disabled="disabled" @click="$emit('add-group')">
        <v-icon class="pagination-filter__glyph">mdi-plus</v-icon>
        Add Filter Group
      </button>
    </div>

    <div v-for="(group, groupIndex) in groups" :key="group.id" class="pagination-filter__group" :data-testid="`${testIdPrefix}__filter-group--${groupIndex}`">
      <div class="pagination-filter__group-header">
        <span class="pagination-filter__group-title">Filter Set {{ groupIndex + 1 }}</span>
        <input
          type="text"
          class="pagination-filter__input pagination-filter__group-name noTransform"
          :data-testid="`${testIdPrefix}__group-name--${groupIndex}`"
          :value="group.name"
          placeholder="Name (optional)"
          :disabled="disabled"
          @input="$emit('update-group-name', group.id, $event.target.value)"
        />
        <button
          type="button"
          class="pagination-filter__remove-btn"
          :data-testid="`${testIdPrefix}__remove-group-btn--${groupIndex}`"
          :disabled="disabled || groups.length === 1"
          :title="groups.length === 1 ? 'Cannot remove the last filter group' : 'Remove this filter group'"
          @click="$emit('remove-group', group.id)"
        >
          <v-icon class="pagination-filter__glyph">mdi-delete-outline</v-icon>
        </button>
      </div>

      <div class="pagination-filter__logic pagination-filter__group-logic">
        <span class="pagination-filter__logic-label">
          Conditions logic: match <strong>{{ group.connector === connectorAnd ? 'ALL' : 'ANY' }}</strong> of the conditions
        </span>
        <div class="pagination-filter__logic-toggle">
          <button
            type="button"
            :data-testid="`${testIdPrefix}__group-logic-and--${groupIndex}`"
            :class="['pagination-filter__logic-btn', { 'pagination-filter__logic-btn--active': group.connector === connectorAnd }]"
            :disabled="disabled"
            @click="$emit('update-group-connector', group.id, connectorAnd)"
          >
            AND
          </button>
          <button
            type="button"
            :data-testid="`${testIdPrefix}__group-logic-or--${groupIndex}`"
            :class="['pagination-filter__logic-btn', { 'pagination-filter__logic-btn--active': group.connector === connectorOr }]"
            :disabled="disabled"
            @click="$emit('update-group-connector', group.id, connectorOr)"
          >
            OR
          </button>
        </div>
      </div>

      <div class="pagination-filter__conditions">
        <div
          v-for="(condition, conditionIndex) in group.conditions"
          :key="condition.id"
          class="pagination-filter__condition"
          :data-testid="`${testIdPrefix}__condition--${groupIndex}-${conditionIndex}`"
        >
          <span class="pagination-filter__condition-index">{{ conditionIndex + 1 }}</span>
          <div class="pagination-filter__select-anchor">
            <v-select
              class="pagination-filter__select pagination-filter__select--column noTransform"
              :data-testid="`${testIdPrefix}__condition-column--${groupIndex}-${conditionIndex}`"
              :value="condition.column || null"
              :items="columnItems"
              item-text="label"
              item-value="value"
              placeholder="Column"
              outlined
              dense
              hide-details
              :disabled="disabled"
              :menu-props="selectMenuProps"
              @mousedown.native="bindMenuScrollClose"
              @keydown.native.down="bindMenuScrollClose"
              @keydown.native.enter="bindMenuScrollClose"
              @keydown.native.space="bindMenuScrollClose"
              @change="emitCondition(group.id, condition.id, 'column', $event)"
            />
          </div>
          <div class="pagination-filter__select-anchor">
            <v-select
              class="pagination-filter__select pagination-filter__select--operator noTransform"
              :data-testid="`${testIdPrefix}__condition-operator--${groupIndex}-${conditionIndex}`"
              :value="condition.operator"
              :items="operatorsFor(condition)"
              item-text="label"
              item-value="value"
              outlined
              dense
              hide-details
              :disabled="disabled"
              :menu-props="selectMenuProps"
              @mousedown.native="bindMenuScrollClose"
              @keydown.native.down="bindMenuScrollClose"
              @keydown.native.enter="bindMenuScrollClose"
              @keydown.native.space="bindMenuScrollClose"
              @change="emitCondition(group.id, condition.id, 'operator', $event)"
            />
          </div>
          <input
            v-if="operatorRequiresValue(condition.operator)"
            :key="`${condition.id}-${valueInputType(condition.column)}`"
            class="pagination-filter__input noTransform"
            :data-testid="`${testIdPrefix}__condition-value--${groupIndex}-${conditionIndex}`"
            :type="valueInputType(condition.column)"
            :value="displayValue(condition)"
            :step="valueInputType(condition.column) === 'number' ? 'any' : undefined"
            placeholder="Enter value"
            :disabled="disabled"
            @input="$emit('update-condition', group.id, condition.id, 'value', $event.target.value)"
          />
          <span v-else class="pagination-filter__null-value">(no value required)</span>
          <button
            type="button"
            class="pagination-filter__remove-btn"
            :data-testid="`${testIdPrefix}__remove-condition-btn--${groupIndex}-${conditionIndex}`"
            title="Remove this condition"
            :disabled="disabled"
            @click="$emit('remove-condition', group.id, condition.id)"
          >
            <v-icon class="pagination-filter__glyph">mdi-close</v-icon>
          </button>
        </div>
      </div>

      <button
        type="button"
        class="pagination-filter__add-condition-btn"
        :data-testid="`${testIdPrefix}__add-condition-btn--${groupIndex}`"
        :disabled="disabled"
        @click="$emit('add-condition', group.id)"
      >
        <v-icon class="pagination-filter__glyph">mdi-plus</v-icon>
        Add Condition
      </button>
    </div>
  </div>
</template>

<script>
import { GROUP_CONNECTOR, operatorRequiresValue, operatorsForType, resolveFieldType } from './PaginationFilterUtils.js';

/**
 * Presentational filter builder. Emits edits; PaginationFilter stores the draft.
 * Field menus use Vuetify's own placement (detached to the app root). The activator
 * sits in a fixed overlay, so Vuetify switches the list to position:fixed and puts it
 * under the control. Nothing here rewrites top/left after open.
 */
export default {
  name: 'PaginationFilterBuilder',

  props: {
    testIdPrefix: {
      type: String,
      default: 'pagination-filter',
    },
    groups: {
      type: Array,
      default: () => [],
    },
    groupingConnector: {
      type: String,
      default: GROUP_CONNECTOR.OR,
    },
    fields: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      connectorAnd: GROUP_CONNECTOR.AND,
      connectorOr: GROUP_CONNECTOR.OR,
      /**
       * Stable object. A new object each render makes v-select remeasure the menu.
       * zIndex sits above the filter scrim (--z-overlay). Vuetify's own stack index does not see that scrim.
       */
      selectMenuProps: {
        offsetY: true,
        bottom: true,
        nudgeBottom: 4,
        closeOnContentClick: true,
        contentClass: 'pagination-filter__select-menu',
        zIndex: 10000,
      },
    };
  },

  computed: {
    columnItems() {
      return (this.fields || []).map((field) => ({
        label: field.label || field.key,
        value: field.key,
      }));
    },
  },

  created() {
    this.menuScrollParent = null;
    this.onMenuScrollParent = null;
    this.menuScrollBound = false;
    this.menuScrollTicket = 0;
    if (typeof window !== 'undefined' && window.getComputedStyle) {
      const menuZ = parseInt(window.getComputedStyle(document.documentElement).getPropertyValue('--z-dialog-menu'), 10);
      if (Number.isFinite(menuZ)) this.selectMenuProps.zIndex = menuZ;
    }
  },

  beforeDestroy() {
    this.releaseMenuScrollClose();
  },

  methods: {
    operatorRequiresValue,

    /**
     * The open list is position:fixed on the app root, so it does not move with the modal body.
     * Listen once, after the open click, and drop the listener as soon as the body scrolls.
     * The handler only closes the menu. It does not write layout.
     */
    bindMenuScrollClose() {
      if (this.menuScrollBound) return;
      const ticket = this.menuScrollTicket + 1;
      this.menuScrollTicket = ticket;
      this.$nextTick(() => {
        if (this.menuScrollTicket !== ticket || this.menuScrollBound || this._isDestroyed) return;
        const parent = this.$el && this.$el.closest ? this.$el.closest('.pagination-filter__modal-body') : null;
        if (!parent) return;
        this.menuScrollParent = parent;
        this.onMenuScrollParent = () => this.closeSelectMenus();
        parent.addEventListener('scroll', this.onMenuScrollParent, { passive: true });
        this.menuScrollBound = true;
      });
    },

    releaseMenuScrollClose() {
      this.menuScrollTicket += 1;
      if (this.menuScrollParent && this.onMenuScrollParent) {
        this.menuScrollParent.removeEventListener('scroll', this.onMenuScrollParent);
      }
      this.menuScrollParent = null;
      this.onMenuScrollParent = null;
      this.menuScrollBound = false;
    },

    closeSelectMenus() {
      this.releaseMenuScrollClose();
      const walk = (vm) => {
        (vm.$children || []).forEach((child) => {
          if (child.$options && child.$options.name === 'v-select' && child.isMenuActive) {
            child.isMenuActive = false;
          }
          walk(child);
        });
      };
      walk(this);
    },

    emitCondition(groupId, conditionId, field, value) {
      this.$emit('update-condition', groupId, conditionId, field, value || '');
      this.$nextTick(() => {
        if (this._isDestroyed || this.selectMenuOpen()) return;
        this.releaseMenuScrollClose();
      });
    },

    selectMenuOpen() {
      let open = false;
      const walk = (vm) => {
        (vm.$children || []).forEach((child) => {
          if (open) return;
          if (child.$options && child.$options.name === 'v-select' && child.isMenuActive) {
            open = true;
            return;
          }
          walk(child);
        });
      };
      walk(this);
      return open;
    },

    operatorsFor(condition) {
      const operators = operatorsForType(resolveFieldType(this.fields, condition.column));
      if (!condition.operator || operators.some((operator) => operator.value === condition.operator)) return operators;
      return operators.concat([{ label: condition.operator, value: condition.operator }]);
    },

    valueInputType(column) {
      const type = resolveFieldType(this.fields, column);
      if (type === 'number') return 'number';
      if (type === 'date') return 'date';
      return 'text';
    },

    /** Date inputs need yyyy-mm-dd. Saved values may arrive as a longer timestamp. */
    displayValue(condition) {
      if (condition.value === null || condition.value === undefined) return '';
      if (resolveFieldType(this.fields, condition.column) !== 'date') return condition.value;
      const match = String(condition.value).match(/^(\d{4}-\d{2}-\d{2})/);
      return match ? match[1] : condition.value;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './PaginationFilter.scss';
</style>
